import _ from "lodash";
import { v4 } from 'uuid';
import { DomUtils, MoModel, MoUtils, QueryStore, QueryStoreConfig } from "ui-common";
import { AGENT_SELECTORS, cacheBeaconConfig, clearStoredCartId, enhancedQuerySelector, facetsConfigIsValid, fetchBeaconConfig, getAgentTypeFromEmbed, getBeaconConfig, getCartId, getClickPosition, getCustomSignalHandlers, getEventPath, getFacets, getFieldValue, getProductIdUsingEmbedActivityConfig, getSavedBeaconConfig, isOptionalProductProp, isProductPage, isRegExpPattern, jsonStringToRegex, mergeConfigWithAttributePriority, mergeConfigWithSavedPriority, searchPageType, setLocalBeaconConfig, signalHandlerKeys, unmatchedPageType } from "../beacon/beacon-helper";
import { BeaconAgentData, BeaconAgentSetupOptions, BeaconConfig, BeaconFieldConfig, DOMEvent, MergedBeaconConfig, RetailQueryStoreConfig, SavedBeaconConfig, SignalHandlerKey } from "./interfaces";
import { EmbedsConfigApiService } from "../embeds-config-api-service";
import { configureStoreApiService } from "../embeds-store-api-helper";
import { RetailQueryStore } from "../../stores/RetailQueryStore";
import { RetailCartAddSignalRequest, RetailClickSignalRequest, RetailProduct, RetailProductDetail, RetailPurchaseSignalRequest, RetailQuerySignalRequest } from "../interfaces";
import { getTimedSessionStorage, setTimedSessionStorage } from "../../utils/timed-storage";

export class BeaconAgent extends MoModel {
  public beaconConfig: MergedBeaconConfig;
  private customSignalHandlers: {[K in SignalHandlerKey]?: (...args: any[]) => any } = {};
  private debouncedQuerySignal: _.DebouncedFunc<(query: string) => void>;
  public defaultLocalApiUrl: string = 'http://localhost:27016/v1';
  public localModeKey: string = 'lwBeaconLocalMode';
  public localApiUrlKey: string = 'lwBeaconLocalApiUrl';
  public localCustomerIdKey: string = 'lwBeaconLocalCustomerId';

  private siteSearchQueryStore: QueryStore;
  private retailQueryStore: RetailQueryStore;
  private configApiService: EmbedsConfigApiService;
  private isRetail: boolean = false;
  private isLocalMode: boolean = false;
  private logVerboseInfo: boolean = localStorage.enableBeaconDev === 'verbose';
  private initialConfig: BeaconConfig;
  private preferProvidedConfig: boolean = false;
  private getEmbedQueryId: () => string;
  private redirectQueryStorageTTL = 5 * 1000;
  private queryIdStorageKey = 'lwBeaconQueryId';
  private lastQueryFromQueryParams: string | null = null;

  constructor(data: BeaconAgentData = {}) {
    super(data, {});

    // Guarded so a second BeaconAgent instance (e.g. the CDN bundle's copy, constructed
    // after this one when the page also uses EmbedsASIntegrator) can't overwrite these
    // with functions bound to an agent that never receives a beacon config.
    if (!window.useLocalBeaconConfig) {
      Object.defineProperty(window, 'useLocalBeaconConfig', {
        value: () => {
          const localConfig = JSON.parse(localStorage.getItem('lwBeaconConfig') || '{}');

          if (!localConfig.config && this.logVerboseInfo) {
            console.warn('Cannot use local beacon config: config not found in local storage.');
            return;
          }

          const oldConfig = this.beaconConfig;
          this.beaconConfig = getBeaconConfig(this.mergeConfig(this.initialConfig, localConfig?.config));
          this.refreshBeaconState(oldConfig);
        },
        writable: false,
        configurable: true,
      })
    }

    if (!window.refreshBeaconConfig) {
      Object.defineProperty(window, 'refreshBeaconConfig', {
        value: async () => {
          const savedConfig = await fetchBeaconConfig(this.configApiService);
          if (savedConfig) {
            cacheBeaconConfig(savedConfig);
          }

          const oldConfig = this.beaconConfig;
          this.beaconConfig = getBeaconConfig(this.mergeConfig(this.initialConfig, savedConfig?.config));
          this.refreshBeaconState(oldConfig);
        },
        writable: false,
        configurable: true
      });
    }

    if (!window.addBeaconListener) {
      Object.defineProperty(window, 'addBeaconListener', {
        value: (signalType: SignalHandlerKey, signalHandler: (...args: any[]) => any) => {
          if (typeof signalType !== 'string') {
            if (this.logVerboseInfo) {
              console.warn('"addBeaconListener" signal type must be a string');
            }
            return;
          }

          if (!signalHandlerKeys.includes(signalType)) {
            if (this.logVerboseInfo) {
              console.warn(`Signal type for handler must be one of: ${signalHandlerKeys.join(', ')}`);
            }
            return;
          }

          if(this.customSignalHandlers[signalType]) {
            if (this.logVerboseInfo) {
              console.warn(`Signal handler for "${signalType}" is already defined.`);
            }
            return;
          }

          if (typeof signalHandler !== 'function') {
            if (this.logVerboseInfo) {
              console.warn('Signal handler must be a function');
            }
            return;
          }
          this.customSignalHandlers[signalType] = signalHandler;
        },
        writable: false,
        configurable: true
      });
    }
  }

  private mergeConfig(attributeConfig: BeaconConfig, savedOrLocalConfig?: BeaconConfig): BeaconConfig {
    return this.preferProvidedConfig
      ? mergeConfigWithAttributePriority(attributeConfig, savedOrLocalConfig)
      : mergeConfigWithSavedPriority(attributeConfig, savedOrLocalConfig);
  }

  async setup(config: BeaconConfig, { getEmbedQueryId = () => '', preferProvidedConfig = false } : BeaconAgentSetupOptions = {}) {
    if (config) {
      this.initialConfig = config;
      this.preferProvidedConfig = preferProvidedConfig;
      const { apiUrl, embedToken } = config.api || {};
      let savedConfig: SavedBeaconConfig = {};

      this.configApiService = new EmbedsConfigApiService({ apiUrl, embedToken });

      savedConfig = await getSavedBeaconConfig(this.configApiService);

      this.beaconConfig = getBeaconConfig(this.mergeConfig(config, savedConfig?.config));

      if (this.debouncedQuerySignal) {
        this.debouncedQuerySignal?.cancel();
      } else {
        this.debouncedQuerySignal = _.debounce((query: string) => {
          if (this.isQueryTrackingAllowed()) {
            this.doSendQuerySignal(query);
          }
        }, this.beaconConfig.query.sendSignalDebounceDelay);
      }

      this.isRetail = _.toLower(config.type) !== 'site_search';
      this.isLocalMode = localStorage[this.localModeKey] === 'true';
      this.getEmbedQueryId = getEmbedQueryId;

      if (savedConfig?.enabled !== false) {
        this.startUpBeacon();
      }

      if (this.beaconConfig?.visit?.enabled) {
        this.sendSiteVisitSignal();
      }
    }
    this.customSignalHandlers = getCustomSignalHandlers(this.beaconConfig);
  }

  /**
   * Smart refresh that only calls necessary functions based on config changes
   * Avoids duplicate event listeners and unnecessary re-initialization
   */
  refreshBeaconState(oldConfig: any) {
    // Always check current page state with new config
    this.trackQueryChanges();
    this.checkCategoryQuery();
    
    // Re-apply config handlers to ensure they take precedence over window function handlers
    this.customSignalHandlers = getCustomSignalHandlers(this.beaconConfig);
    
    // Only re-setup query triggers if trigger selector changed
    const oldTrigger = oldConfig?.query?.trigger;
    const newTrigger = this.beaconConfig?.query?.trigger;
    
    if (oldTrigger !== newTrigger) {
      this.sendInitialQueryFromTriggerInput();
    }
    
    // Re-setup bulk request only if bulk settings changed
    const oldBulkInterval = oldConfig?.attributes?.bulkSignalsInterval;
    const newBulkInterval = this.beaconConfig?.attributes?.bulkSignalsInterval;
    const bulkRequest = this.isRetail && newBulkInterval > 0;
    
    if (oldBulkInterval !== newBulkInterval && bulkRequest) {
      this.getRetailQueryStore();
      this.retailQueryStore.bulkRequest();
    }
  }

  startUpBeacon() {
    /**
     * [Bulk Request]: Instantiate query store right away and flush the potential pending requests.
     */
    const bulkRequest = this.isRetail && this.beaconConfig.attributes?.bulkSignalsInterval > 0;

    if (bulkRequest) {
      this.getRetailQueryStore();
      this.retailQueryStore.bulkRequest();
    }

    /**
     * [Deep Linking]: Monitor query changes resulting from URL query parameter updates.
     */
    this.trackQueryChanges();
    MoUtils.trackHistoryStateChange(() => this.trackQueryChanges());

    this.checkCategoryQuery();

    /**
     * [custom Query Elements]: Monitor query changes in search-related elements, such as the search input, button, and icon.
     */
    this.sendInitialQueryFromTriggerInput();

    // These are stable references (created once, as class fields) rather than inline closures, so
    // a repeat startUpBeacon() call (e.g. setup() called again on the same shared beaconAgent) doesn't
    // stack duplicate listeners — the DOM discards an addEventListener call with an identical
    // reference and options instead of registering another one.
    // Ensures these handlers execute before the registered listeners from deeper elements in the DOM.
    document.addEventListener(`click`, this.onDocumentClick, { capture: true });
    document.addEventListener(`keydown`, this.onDocumentKeydown, { capture: true });

    // Listen for agent response completion events from embeddable agents
    if (this.beaconConfig.embeddable_activity?.enabled) {
      document.addEventListener('lw-agent-response-complete', this.onAgentResponseCompleteListener);
    }

    this.checkDevSetup();
  }

  isQueryTrackingAllowed(): boolean {
    let allowUrls = this.beaconConfig.query?.allowUrls;
    let isAllowed = _.isEmpty(allowUrls);

    if (!isAllowed) {
      const windowTop = MoUtils.getWindowTop();
      const url = windowTop.document.location.href;

      allowUrls = _.isArray(allowUrls) ? allowUrls : [];
      isAllowed = _.some(allowUrls, (allowedUrl) => _.includes(url, allowedUrl));
    }

    return isAllowed;
  }

  // Only matches native <input> elements. Custom elements wrapping inputs must be targeted at the inner <input>.
  getQueryInputElement = () => {
    return _.find(enhancedQuerySelector(this.beaconConfig.query.trigger), { tagName: 'INPUT' });
  };

  sendInitialQueryFromTriggerInput = () => {
    const query = this.getQueryFromInput();

    if (query) {
      this.sendQuerySignal();
    }
  };

  getQueryParams = () => {
    const queryParams = _.isArray(this.beaconConfig.query.queryParam) 
      ? this.beaconConfig.query.queryParam 
      : [this.beaconConfig.query.queryParam];

    // Get default query from URL query params
    return _.reduce(queryParams, (result, param) => {
      return result || MoUtils.getUrlQueryParams(param);
    }, null);
  };

  sendEmbedProductSignal() {
    const embeddableActivityConfig = this.beaconConfig.embeddable_activity;
    // check to see if an agent is present on page
    const embeddableElement = document.querySelector(AGENT_SELECTORS);

    // gather other necessary info for signal
    const productId = getProductIdUsingEmbedActivityConfig(embeddableActivityConfig);
    let agentType = null;
    if (embeddableElement) {
      agentType = getAgentTypeFromEmbed(embeddableElement);
    }

    this.retailQueryStore.sendEmbedProductSignal({
      componentLoaded: !!embeddableElement,
      productId,
      ...(agentType && { agentType }),
    });
  }

  trackQueryChanges = async() => {
    const url = MoUtils.getWindowTop().document.location.href;
    let shouldSendQuerySignal = false;

    // Check if query param is present in URL
    let query = this.getQueryParams();

    // if embeddable interaction signal is enabled and it's a product details page, send product embeddable signal
    const embeddableActivityConfig = this.beaconConfig.embeddable_activity;
    if (embeddableActivityConfig?.enabled && isProductPage(embeddableActivityConfig)) {
      await MoUtils.waitFor(embeddableActivityConfig.delayForRedirect);
      this.sendEmbedProductSignal();
    }

    // If query param is the same as previous, set to undefined to avoid duplicate signals
    if (query && query === this.lastQueryFromQueryParams) {
      query = undefined;
    } else if (query) {
      this.lastQueryFromQueryParams = query;
    }

    // If the query is not found in URL, check if it was stored in session storage from search redirects.
    query ??= (getTimedSessionStorage('lwCurrentQuery') || undefined);

    shouldSendQuerySignal = !!query;

    // check if URL pattern is present in URL
    const queryUrlPattern = this.beaconConfig.query?.urlPattern;
    if (queryUrlPattern) {
      const isRegExp = isRegExpPattern(queryUrlPattern);
      if (isRegExp) {
        const regexp = jsonStringToRegex(queryUrlPattern);
        if (url.match(regexp)) {
          shouldSendQuerySignal = true;
        }
      } else if (_.includes(url, queryUrlPattern)) {
        shouldSendQuerySignal = true;
      }
    }
    
    const purchaseCompleteConfig = this.beaconConfig?.purchase_complete;

    if (purchaseCompleteConfig && purchaseCompleteConfig?.enabled !== false && _.includes(url, purchaseCompleteConfig?.urlPattern)) {
      await MoUtils.waitFor(purchaseCompleteConfig.delayForPurchaseRedirect);
      this.sendRetailPurchaseSignal(true);
    }

    if (shouldSendQuerySignal) {
      this.sendQuerySignal(query);
    }
  };

  getPageType(pageHasProducts: boolean) {
    const pageTypesConfig = this.beaconConfig.query.pageTypes;
    const url = MoUtils.getWindowTop().document.location.href;

    const pageType = _.toUpper(_.findKey(pageTypesConfig, (patterns) => {
      return _.some(patterns, (pattern) => {
        return (isRegExpPattern(pattern) && url.match(pattern.replace(/(^\/|\/$)/g, '')) || url === pattern);
      });
    }));

    return pageType || (pageHasProducts ? searchPageType : unmatchedPageType);
  }

  async checkCategoryQuery() {
    const categoryPageQueryMap = this.beaconConfig.query.categoryPageQueryMap;

    if (categoryPageQueryMap) {
      const url = MoUtils.getWindowTop().document.location.href;
      let categoryQuery: string;

      _.some(categoryPageQueryMap, (query, keyword) => {
        const match = _.includes(url, keyword);

        categoryQuery = match ? query : null;

        return match;
      });

      if (categoryQuery) {
        this.sendQuerySignal(categoryQuery);
      }
    }
  }

  sendSiteVisitSignal() {
    this.getRetailQueryStore(true).siteVisitSignal({
      uri: MoUtils.getWindowTop().location.href,
    });

    if (this.logVerboseInfo) {
      console.log('👀 Site Visit Signal Sent:\n\nURL:', MoUtils.getWindowTop().location.href);
    }
  }

  getQueryFromInput = () => {
    return _.get(this.getQueryInputElement(), 'value');
  };

  sendQuerySignal = async (query: string = this.getQueryFromInput() || this.getQueryParams()) => {
    if (!_.isEmpty(query)) {
      setTimedSessionStorage('lwCurrentQuery', query, this.redirectQueryStorageTTL);
    }

    await MoUtils.waitFor(this.beaconConfig.query.delayForTrackQueryUpdate);
    this.debouncedQuerySignal(query);
  };

  getEnrichedPayload = (signalType: SignalHandlerKey, payload: any): false | RetailQuerySignalRequest | RetailClickSignalRequest | RetailCartAddSignalRequest | RetailPurchaseSignalRequest => {
    const payloadClone = _.cloneDeep(payload);
    let enrichedPayload: any;
    try {
      enrichedPayload = this.customSignalHandlers[signalType](payloadClone);
    } catch (e) {
      if (this.logVerboseInfo) {
        console.error(`Error in custom ${signalType} signal handler. Original signal will be sent instead.`, e);
      }
    }
    if (enrichedPayload) {
      return enrichedPayload;
    }
    if (enrichedPayload === false) {
      if (this.logVerboseInfo) {
        console.log(`${_.capitalize(signalType)} signal sending stopped by custom signal handler`);
      }
      payload = false;
    }
    return payload;
  };

  doSendQuerySignal(query: string) {
    const products = enhancedQuerySelector(this.getProductSelector());
    const searchQuery = query || null;
    const props = { searchTerms: searchQuery } as RetailQuerySignalRequest;

    if (this.isRetail) {
      if (this.beaconConfig.query.facets && facetsConfigIsValid(this.beaconConfig, this.logVerboseInfo)) {
        const facets = getFacets(this.beaconConfig, this.logVerboseInfo);
        if(!_.isEmpty(facets)) {
          props.selected_facets = facets;
        }
      } 

      const productDetails = _.map(products, (product) => this.getRetailProductDetails(product, this.beaconConfig.query.fields));

      const pageTypesConfig = this.beaconConfig.query.pageTypes;
      if(pageTypesConfig) {
        const pageHasProducts = !_.isEmpty(productDetails); 
        const pageType = this.getPageType(pageHasProducts);
        if (pageType !== searchPageType) {
          props.pageType = pageType;
        } 
      }

      let payload: RetailQuerySignalRequest | boolean = {
        ...props,
        productDetails: this.filterProductDetails(productDetails),
        labels: this.beaconConfig.labels,
      }

      if (this.customSignalHandlers.query) {
        payload = this.getEnrichedPayload('query', payload) as RetailQuerySignalRequest | false;
        if (!payload) {
          return;
        }
      }

      this.getRetailQueryStore(true).querySignal(payload as RetailQuerySignalRequest);

      if (this.logVerboseInfo) {
        const pageType = _.get(payload, 'pageType');
        const facets = _.get(payload, 'selected_facets');
        console.log('🔍 Query Signal Sent:\n\nProduct Details:', (payload as RetailQuerySignalRequest).productDetails, '\nQuery:', (payload as RetailQuerySignalRequest).searchTerms, pageType ? `\nPage Type: ${pageType}` : '', facets ? `\nFacets:` : '', facets ? facets : ''); 
      }

      delete sessionStorage.lwCurrentQuery;
    } else {
      this.getSiteSearchQueryStore(true).querySignal({
        documentIds: _.map(products, (product) => this.getProductId(product)),
        ...props,
      });
    }
  }

  isSignalTypeTrigger(element: HTMLElement, signalType: string) {
    const trigger = this.beaconConfig[signalType]?.trigger;
    const triggerElements = enhancedQuerySelector(trigger);
    return _.includes(triggerElements, element) || DomUtils.selectParent(element, trigger);
  }

  onAgentResponseComplete(detail: { question: string; response: string; sourceId: string; questionPosition?: number }) {
    const { question, response, sourceId, questionPosition } = detail;
    const embeddableActivityConfig = this.beaconConfig.embeddable_activity;

    const productId = getProductIdUsingEmbedActivityConfig(embeddableActivityConfig);

    this.getRetailQueryStore().sendEmbedInteractionSignal({
      sourceId,
      productId,
      conversationalQA: [{ question, response }],
      ...(questionPosition && { selectedPosition: questionPosition })
    });
  }

  // Stable references (created once as class fields) for startUpBeacon()'s document listeners, so a repeat
  // setup() call on the same shared beaconAgent doesn't stack duplicate listeners.
  onDocumentClick = (event: DOMEvent) => this.onPageClick(event);
  onDocumentKeydown = (event: DOMEvent) => this.onPageEnter(event);
  onAgentResponseCompleteListener = ((event: CustomEvent) => {
    this.onAgentResponseComplete(event.detail);
  }) as EventListener;

  /**
   * Gets the actual target element from an event, supporting both regular DOM and shadow DOM.
   * Uses composedPath()[0] when available (shadow DOM events), otherwise falls back to event.target.
   */
  getEventTarget = (event: DOMEvent): HTMLElement => {
    const actualTarget = event.composedPath ? event.composedPath()[0] : null;

    return (actualTarget || event.target) as HTMLElement;
  };

  onPageClick = (event: DOMEvent) => {
    const element = this.getEventTarget(event);
    const targets = getEventPath(event);

    const handlers = {
      click: () => this.trySendingClickSignal(element),
      cartAdd: () => this.sendRetailCartAddSignal(element),

      purchase: () => {
        if (_.get(this.beaconConfig, 'purchase.enabled', true)) {
          this.sendRetailPurchaseSignal(false);
        }
      },

      query: () => {
        if (this.getQueryInputElement() !== element) {
          this.sendQuerySignal();
        }
      },
    };

    const matched = _.some(handlers, (handler, type) => {
      if (_.some(targets, (target) => this.isSignalTypeTrigger(target, type))) {
        handler();
        return true;
      }
    });

    if (!matched && !this.beaconConfig.click?.trigger) {
      this.trySendingClickSignal(element);
    }
  };

  onPageEnter = (event: DOMEvent) => {
    if (event.key !== 'Enter') return;

    const queryInput = this.getQueryInputElement();
    const matched = _.find(getEventPath(event), (el) => el === queryInput) as HTMLInputElement;

    if (matched?.value) {
      this.sendQuerySignal(matched.value);
    }
  };

  filterProductDetails(productDetails: RetailProductDetail[]) {
    return _.filter(productDetails, 'product.productId');
  }

  getProductSelector(path?: string) {
    return path || this.beaconConfig.attributes.productPath;
  }

  sendRetailPurchaseSignal(completePurchase: boolean) {
    const purchaseConfig: any = completePurchase ? this.beaconConfig.purchase_complete : this.beaconConfig.purchase;
    const handlerKey: SignalHandlerKey = completePurchase ? 'purchase_complete' : 'purchase';
    const items = enhancedQuerySelector(this.getProductSelector(purchaseConfig.productPath));
    let productDetails = _.map(items, (product) => this.getRetailProductDetails(product, purchaseConfig.fields));
    let revenue: number | undefined;
    let orderId: string | undefined;

    if (completePurchase) {
      if (purchaseConfig?.orderIdUrlParam) {
        orderId = MoUtils.getUrlQueryParams(purchaseConfig.orderIdUrlParam);
      } else if(purchaseConfig?.orderIdSelector) {
        orderId = this.getValueFromPathConfig(document, purchaseConfig.orderIdSelector, 'orderId');
      }
    }

    productDetails = this.filterProductDetails(productDetails);

    const hasHandler = !!this.customSignalHandlers[handlerKey];

    // Proceed if the DOM scrape found products or a handler exists that might supply its own.
    if (_.isEmpty(productDetails) && !hasHandler) {
      return;
    }

    // Only compute revenue from the DOM when there are DOM-scraped productDetails to sum.
    // Don't let the guard fallback fabricate a fake revenue when a handler is expected
    // to supply the real value (e.g. from window.dataLayer) on a page with no product markup.
    if (!_.isEmpty(productDetails)) {
      if (purchaseConfig.revenue) {
        revenue = this.getValueFromPathConfig(document, purchaseConfig.revenue, 'revenue');
      }

      // Make it default to none zero number just for guarding the error on dev env which should be fine with the retail app.
      revenue ||= _.sumBy(productDetails, (item) => (item.quantity || 1) * (item.product.price || 0)) || 0.1;
    }

    let payload: RetailPurchaseSignalRequest | false = {
      productDetails,
      revenue,
      orderId,
      cartId: getCartId(this.beaconConfig),
      labels: this.beaconConfig.labels,
    };

    if (hasHandler) {
      payload = this.getEnrichedPayload(handlerKey, payload) as RetailPurchaseSignalRequest | false;
      if (!payload) {
        return;
      }
    }

    // Never send a signal whose productDetails end up empty - whether that's because there
    // was no DOM data and no handler rescued it, or a handler ran but didn't supply any either.
    if (_.isEmpty(payload.productDetails)) {
      return;
    }

    this.sendPurchaseSignal({
      ...payload as RetailPurchaseSignalRequest,
      orderId: (payload as RetailPurchaseSignalRequest).orderId || '' // Ensure orderId is provided, defaulting to an empty string if undefined
    });
  }

  sendRetailCartAddSignal(element) {
    let productElement = this.getProductFromParent(element, this.beaconConfig.cartAdd.productPath);

    productElement ??= _.first(enhancedQuerySelector(this.beaconConfig.cartAdd.productPath));

    if (productElement) {
      const productDetail = this.getRetailProductDetails(productElement, this.beaconConfig.cartAdd.fields);
      const idFromUrl = MoUtils.getUrlQueryParams(this.beaconConfig.attributes.idQueryParam);
      const uri = MoUtils.getURI();

      productDetail.product.productId ||= idFromUrl || uri;
      productDetail.product.uri ||= uri;

      let payload: RetailCartAddSignalRequest | false = {
        productDetails: [productDetail],
        cartId: getCartId(this.beaconConfig),
        labels: this.beaconConfig.labels,
      }

      if (this.customSignalHandlers.cartAdd) {
        payload = this.getEnrichedPayload('cartAdd', payload) as RetailCartAddSignalRequest | false;
        if (!payload) {
          return;
        }
      }

      this.getRetailQueryStore().cartAddSignal(payload as RetailCartAddSignalRequest);

      if (this.logVerboseInfo) {
        console.log('🛒 Cart Add Signal Sent:\n\nProduct Details:', productDetail, '\nElement:', element);
      }
    }
  }

  async sendPurchaseSignal({ productDetails, revenue, cartId, orderId, labels }: RetailPurchaseSignalRequest) {
    await this.getRetailQueryStore().purchaseSignal({
      productDetails,
      revenue,
      cartId,
      labels,
      ...(orderId && { orderId: orderId })
    });

    clearStoredCartId(this.beaconConfig);

    if (this.logVerboseInfo) {
      console.log('🛒 Purchase Signal Sent:\n\nProduct Details:', productDetails, '\nrevenue:', revenue, orderId ? `\nOrder ID: ${orderId}` : '');
    }
  }

  trySendingClickSignal(element) {
    const productPath = this.beaconConfig.click.productPath;
    const products = enhancedQuerySelector(this.getProductSelector(productPath));
    const elementIsProduct = _.includes(products, element);
    let product;
    let id;

    product = elementIsProduct ? element : this.getProductFromParent(element, productPath);

    if (product) {
      const fields: any = this.getRetailProductDetails(product);

      id = fields?.product?.productId;
      element = product;
    }

    if (id) {
      if (this.isRetail) {
        const indexOnPage = _.indexOf(products, element);
        const selectedPosition = getClickPosition(indexOnPage, this.beaconConfig);

        let payload: RetailClickSignalRequest | false = { productDetails: [this.getRetailProductDetails(element, this.beaconConfig.click.fields)], selectedPosition, labels: this.beaconConfig.labels };
        if (this.customSignalHandlers.click) {
          payload = this.getEnrichedPayload('click', payload);
          if (!payload) {
            return;
          }
        }

        if (selectedPosition > 0) {
          (payload as RetailClickSignalRequest).selectedPosition = selectedPosition;
        }

        this.getRetailQueryStore().clickSignal(payload as RetailClickSignalRequest);

        if (this.logVerboseInfo) {
          console.log('🖱️ Click Signal Sent:\n\nRequest:', payload, '\nElement:', element);
        }
      } else {
        this.getSiteSearchQueryStore().clickSignal({ documentId: id });
      }
    }
  }

  getProductFromParent(element, productPath?) {
    return DomUtils.selectParent(element, this.getProductSelector(productPath));
  }

  getProductId(element, path: BeaconFieldConfig = this.beaconConfig.fields.productId) {
    return this.getValueFromPathConfig(element, path);
  }

  getValueFromPathConfig(element, fieldConfig, field?) {
    const pathConfig = _.isPlainObject(fieldConfig) ? fieldConfig.path : fieldConfig;
    const paths = (pathConfig || '').split(',').map(_.trim);
    let value;

    _.some(_.compact(paths), (path) => value = getFieldValue(element, path, field, fieldConfig, this.beaconConfig));

    return value;
  }

  getRetailProductDetails(element, fieldsConfig = this.beaconConfig.fields): RetailProductDetail {
    const product: RetailProduct = _.mapValues(fieldsConfig, (fieldPath, field) => this.getValueFromPathConfig(element, fieldPath, field));
    const quantity = product.quantity;

    const productDetail: RetailProductDetail = {
      product: _.omitBy(product, (value, key) => {
        return (!value && isOptionalProductProp(key)) || _.isNaN(value);
      }),
    }

    if (quantity) {
      productDetail.quantity = quantity;
      delete productDetail.product.quantity;
    }

    return productDetail;
  }

  getQueryStore<T extends QueryStore>(storeClass: new (config: QueryStoreConfig) => T, newQuery: boolean = false): T {
    let store: T;
  
    const { apiUrl, embedToken } = this.beaconConfig.api;
    const configureStoreApiServiceOptions = { apiUrl, embedToken, headers: null };
    let apiService;

    if (this.isLocalMode) {
      const customerId = localStorage[this.localCustomerIdKey];

      if (customerId) {
        configureStoreApiServiceOptions.apiUrl = localStorage[this.localApiUrlKey] || this.defaultLocalApiUrl;

        configureStoreApiServiceOptions.headers = {
          'x-lw-customer-uuid': customerId,
          'x-lw-app-uuid': EmbedsConfigApiService.extractAppIdFromAppPath(apiUrl || ''),
        };
      }
    }

    apiService = configureStoreApiService(configureStoreApiServiceOptions, false);
  
    const storeConfig: RetailQueryStoreConfig = {
      type: 'search',
      bulkSignalsInterval: this.beaconConfig.attributes?.bulkSignalsInterval || 0,
      beaconConfig: this.beaconConfig,
    };
  
    store = new storeClass(storeConfig);
    store.getApiService = () => apiService;
  
    // Set new query ID or restore from session storage
    const storedQueryId = newQuery ? null : sessionStorage.getItem(this.queryIdStorageKey);

    if (storedQueryId) {
      store.setQueryId(storedQueryId);
    } else {
      this.setNewQueryId(store);
    }
  
    return store;
  }

  getSiteSearchQueryStore(newQuery: boolean = false, force: boolean = false): QueryStore {
    if (!this.siteSearchQueryStore || force) {
      this.siteSearchQueryStore = this.getQueryStore(QueryStore, newQuery);
    }

    return this.siteSearchQueryStore;
  }

  getRetailQueryStore(newQuery: boolean = false, force: boolean = false): RetailQueryStore {
    if (this.retailQueryStore && newQuery) {
      this.setNewQueryId(this.retailQueryStore);
    }
    
    if (!this.retailQueryStore || force) {
      this.retailQueryStore = this.getQueryStore(RetailQueryStore, newQuery);
    }

    return this.retailQueryStore;
  }

  setNewQueryId(store: QueryStore) {
    const queryId = this.getEmbedQueryId() || v4();

    store.setQueryId(queryId);
    sessionStorage.setItem(this.queryIdStorageKey, queryId);
  }

  checkDevSetup() {
    const devEnabled = localStorage.enableBeaconDev === 'true';

    if (this.logVerboseInfo || devEnabled) {
      this.logInfo();
      window.logBeaconInfo = () => this.logInfo();

      if (devEnabled) {
        window.setLocalBeaconConfig = setLocalBeaconConfig;
      }
    }
  }

  logInfo() {
    const queryProducts = enhancedQuerySelector(this.getProductSelector());
    const cartAddElements = enhancedQuerySelector(this.beaconConfig.cartAdd?.trigger || '');
    const purchaseElements = enhancedQuerySelector(this.beaconConfig.purchase?.trigger || '');
    const cartItems = enhancedQuerySelector(this.getProductSelector(this.beaconConfig.purchase.productPath));
    const purchaseProductPath = this.beaconConfig.purchase_complete?.productPath;
    const purchaseItems = enhancedQuerySelector(purchaseProductPath ? this.getProductSelector(purchaseProductPath) : null);
    const queryProductsDetails = _.map(queryProducts, (product) => this.getRetailProductDetails(product, this.beaconConfig.query.fields))
    const cartItemDetails = _.map(cartItems, (product) => this.getRetailProductDetails(product, this.beaconConfig.purchase.fields))
    const purchaseItemDetails = _.map(purchaseItems, (product) => this.getRetailProductDetails(product, this.beaconConfig.purchase_complete?.fields));

    console.log(`=== 📡 Beacon Debug Mode Enabled 📡 ====`);
    console.log(`📝 This Beacon info can be reported via "window.logBeaconInfo()"`, '\n');
    console.log(`❇ Beacon Config:`, this.beaconConfig);
    console.log(`❇ Query signal handler:`, this.customSignalHandlers.query);
    console.log(`❇ Click signal handler:`, this.customSignalHandlers.click);
    console.log(`❇ Add to cart signal handler:`, this.customSignalHandlers.cartAdd);
    console.log(`❇ Purchase signal handler:`, this.customSignalHandlers.purchase);
    console.log(`❇ Purchase complete signal handler:`, this.customSignalHandlers.purchase_complete);
    console.log(`❇ Add to cart triggers:`, cartAddElements);
    console.log(`❇ Purchase triggers:`, purchaseElements);
    console.log(`❇ Products in main query list:`, queryProducts, '\n', '❇ Product Details:', queryProductsDetails);
    console.log(`❇ Products in cart:`, cartItems, '\n', '❇ Cart Item Details:', cartItemDetails);
    console.log(`❇ Products purchased:`, purchaseItems, '\n', '❇ Purchased Product Details:', purchaseItemDetails);

    if (this.isLocalMode) {
      console.log(`\n=== 📌 Beacon Local Mode Enabled 📌 ====`);
      console.log(`❇ API URL:`, localStorage[this.localApiUrlKey] || this.defaultLocalApiUrl);
      console.log(`❇ Customer ID:`, localStorage[this.localCustomerIdKey]);
    }

    console.log(`\n\n🔩 To enable local mode sending signals to localhost for backend local dev purposes:\n\n`);
    console.log(`localStorage.${this.localModeKey} = 'true'`);
    console.log(`localStorage.${this.localCustomerIdKey} = __customer_id__\n\n`);
    console.log(`/** Default to ${this.defaultLocalApiUrl} */`);
    console.log(`localStorage.${this.localApiUrlKey} = __local_api_host__`);
  }
}

export const beaconAgent = new BeaconAgent();